const ATTRIBUTE_KEYS = [
  'Tipo de compra',
  'Tipo de evento',
  'Nombre de la escuela',
  'Nombre del graduado',
  'Número de mesa',
  'Fecha de la graduación',
];

const CHECKOUT_TRIGGER = 'button[name="checkout"], input[name="checkout"], [data-mrb-checkout]';

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * @typedef {object} EventData
 * @property {string} eventType
 * @property {string} [eventTypeOther]
 * @property {string} [schoolName]
 * @property {string} [graduateName]
 * @property {string} [tableNumber]
 * @property {string} [eventDate]
 */

/**
 * Cart attributes for the purchase type, keyed exactly like the live storefront.
 * @param {EventData | null} data
 * @param {{ otherValue?: string, graduationValue?: string }} [options]
 * @returns {{ key: string, value: string }[]}
 */
export function purchaseAttributes(data, { otherValue = 'Otro', graduationValue = 'Graduación' } = {}) {
  if (!data) return [{ key: 'Tipo de compra', value: 'Personal' }];
  const eventType = data.eventType !== otherValue ? data.eventType : (data.eventTypeOther ?? '').trim() || otherValue;
  const attributes = [
    { key: 'Tipo de compra', value: 'Evento' },
    { key: 'Tipo de evento', value: eventType },
  ];
  if (data.eventType !== graduationValue) return attributes;
  return [
    ...attributes,
    { key: 'Nombre de la escuela', value: data.schoolName ?? '' },
    { key: 'Nombre del graduado', value: data.graduateName ?? '' },
    { key: 'Número de mesa', value: data.tableNumber ?? '' },
    { key: 'Fecha de la graduación', value: data.eventDate ?? '' },
  ];
}

/**
 * `/cart/update.js` merges attributes, so keys that no longer apply are sent blank to remove them.
 * @param {{ key: string, value: string }[]} attributes
 */
export function attributesPayload(attributes) {
  /** @type {Record<string, string>} */
  const payload = {};
  for (const key of ATTRIBUTE_KEYS) payload[key] = '';
  for (const { key, value } of attributes) payload[key] = value;
  return payload;
}

/** @param {string} value */
function maskDate(value) {
  const numbers = value.replace(/\D/g, '').slice(0, 8);
  if (numbers.length <= 2) return numbers;
  if (numbers.length <= 4) return `${numbers.slice(0, 2)}/${numbers.slice(2)}`;
  return `${numbers.slice(0, 2)}/${numbers.slice(2, 4)}/${numbers.slice(4)}`;
}

/** @param {string} value dd/MM/yyyy */
function toIsoDate(value) {
  const match = value.trim().match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!match) return '';
  const [, dd, mm, yyyy] = match;
  const date = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
  if (date.getFullYear() !== Number(yyyy) || date.getMonth() !== Number(mm) - 1 || date.getDate() !== Number(dd)) return '';
  return `${yyyy}-${mm}-${dd}`;
}

/** @param {string} iso yyyy-MM-dd */
function fromIsoDate(iso) {
  const [yyyy, mm, dd] = iso.split('-');
  return yyyy && mm && dd ? `${dd}/${mm}/${yyyy}` : '';
}

class MrbPurchaseType extends HTMLElement {
  #submitted = false;
  #loading = false;
  /** @type {HTMLElement | null} */
  #trigger = null;

  connectedCallback() {
    this.dialog = /** @type {HTMLDialogElement} */ (this.querySelector('[data-pt-dialog]'));
    this.form = /** @type {HTMLFormElement} */ (this.querySelector('[data-pt-form]'));
    if (!this.dialog || !this.form) return;

    this.eventPanel = this.querySelector('[data-pt-event]');
    this.otherPanel = this.querySelector('[data-pt-other]');
    this.graduationPanel = this.querySelector('[data-pt-graduation]');
    this.quotePanel = this.querySelector('[data-pt-quote]');
    this.confirmButton = /** @type {HTMLButtonElement} */ (this.querySelector('[data-pt-confirm]'));
    this.confirmLabel = /** @type {HTMLElement} */ (this.querySelector('[data-pt-confirm-label]'));
    this.dateText = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-pt-date-text]'));
    this.dateNative = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-pt-date-native]'));

    document.addEventListener('click', this.#onDocumentClick, true);
    document.addEventListener('submit', this.#onDocumentSubmit, true);
    window.addEventListener('pageshow', this.#onPageShow);

    this.form.addEventListener('change', this.#onFormChange);
    this.form.addEventListener('input', this.#onFormInput);
    this.form.addEventListener('submit', this.#onConfirm);
    this.querySelectorAll('[data-pt-cancel]').forEach((button) => button.addEventListener('click', this.#onCancel));
    this.querySelector('[data-pt-quote-link]')?.addEventListener('click', this.#onQuote);
    this.querySelector('[data-pt-date-button]')?.addEventListener('click', this.#openDatePicker);
    this.dateNative?.addEventListener('change', this.#onNativeDate);
    this.dialog.addEventListener('cancel', this.#onEscape);
    this.dialog.addEventListener('click', this.#onBackdropClick);
    this.dialog.addEventListener('keydown', this.#trapFocus);

    if (window.Shopify?.designMode) {
      document.addEventListener('shopify:section:select', this.#onEditorSelect);
      document.addEventListener('shopify:section:deselect', this.#onEditorDeselect);
    }
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.#onDocumentClick, true);
    document.removeEventListener('submit', this.#onDocumentSubmit, true);
    document.removeEventListener('shopify:section:select', this.#onEditorSelect);
    document.removeEventListener('shopify:section:deselect', this.#onEditorDeselect);
    window.removeEventListener('pageshow', this.#onPageShow);
  }

  /** Previews the modal while its section is selected in the theme editor. */
  #onEditorSelect = (event) => {
    if (event.target instanceof Node && event.target.contains(this)) this.open();
  };

  #onEditorDeselect = (event) => {
    if (event.target instanceof Node && event.target.contains(this)) this.close();
  };

  get otherValue() {
    return this.dataset.otherValue || 'Otro';
  }

  get graduationValue() {
    return this.dataset.graduationValue || 'Graduación';
  }

  get purchaseType() {
    const checked = /** @type {HTMLInputElement | null} */ (this.form.querySelector('input[name="purchase_type"]:checked'));
    return checked?.value === 'evento' ? 'evento' : 'personal';
  }

  get eventType() {
    const select = /** @type {HTMLSelectElement | null} */ (this.form.elements.namedItem('eventType'));
    return select?.value ?? '';
  }

  /** @param {Event} event */
  #isCheckoutEvent(event) {
    const target = event.target instanceof Element ? event.target : null;
    const trigger = target?.closest(CHECKOUT_TRIGGER);
    if (!trigger || this.contains(trigger)) return null;
    if (trigger instanceof HTMLButtonElement && trigger.disabled) return null;
    return /** @type {HTMLElement} */ (trigger);
  }

  #onDocumentClick = (event) => {
    const trigger = this.#isCheckoutEvent(event);
    if (!trigger) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    this.open(trigger);
  };

  #onDocumentSubmit = (event) => {
    const submitter = /** @type {SubmitEvent} */ (event).submitter;
    if (!(submitter instanceof HTMLElement) || !submitter.matches(CHECKOUT_TRIGGER) || this.contains(submitter)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    this.open(submitter);
  };

  /** @param {HTMLElement | null} [trigger] */
  open(trigger = null) {
    if (this.dialog.open) return;
    this.#trigger = trigger;
    this.#reset();
    this.dialog.showModal();
    document.documentElement.classList.add('mrb-pt-open');
    /** @type {HTMLInputElement | null} */ (this.form.querySelector('input[name="purchase_type"]:checked'))?.focus();
  }

  close() {
    if (!this.dialog.open) return;
    this.dialog.close();
    document.documentElement.classList.remove('mrb-pt-open');
    this.#reset();
    if (this.#trigger?.isConnected) this.#trigger.focus({ preventScroll: true });
    this.#trigger = null;
  }

  #reset() {
    this.#submitted = false;
    this.#setLoading(false);
    this.form.reset();
    this.#sync();
  }

  #onPageShow = (event) => {
    if (!event.persisted) return;
    this.#setLoading(false);
    this.close();
  };

  #onCancel = () => {
    if (this.#loading) return;
    this.close();
  };

  #onEscape = (event) => {
    event.preventDefault();
    if (!this.#loading) this.close();
  };

  #onBackdropClick = (event) => {
    if (event.target === this.dialog && !this.#loading) this.close();
  };

  #onQuote = () => {
    this.close();
    const drawer = /** @type {(HTMLElement & { close?: () => void }) | null} */ (
      document.querySelector('cart-drawer-component')
    );
    drawer?.close?.();
  };

  /** @param {KeyboardEvent} event */
  #trapFocus = (event) => {
    if (event.key !== 'Tab') return;
    const tabbable = [...this.dialog.querySelectorAll(FOCUSABLE)].filter(
      (el) =>
        el instanceof HTMLElement &&
        el.getClientRects().length > 0 &&
        !(el instanceof HTMLInputElement && el.type === 'radio' && !el.checked)
    );
    if (tabbable.length === 0) return;
    const first = /** @type {HTMLElement} */ (tabbable[0]);
    const last = /** @type {HTMLElement} */ (tabbable[tabbable.length - 1]);
    const active = document.activeElement;
    if (event.shiftKey && (active === first || !this.dialog.contains(active))) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && (active === last || !this.dialog.contains(active))) {
      event.preventDefault();
      first.focus();
    }
  };

  #onFormChange = (event) => {
    const target = event.target;
    if (target instanceof HTMLInputElement && target.name === 'purchase_type') {
      this.#sync();
      return;
    }
    if (target instanceof HTMLSelectElement && target.name === 'eventType') this.#sync();
    if (this.#submitted) this.#validate();
  };

  #onFormInput = (event) => {
    const target = event.target;
    if (target === this.dateText && this.dateText) {
      const masked = maskDate(this.dateText.value);
      if (masked !== this.dateText.value) this.dateText.value = masked;
      if (this.dateNative) this.dateNative.value = toIsoDate(masked);
    }
    if (this.#submitted) this.#validate();
  };

  #openDatePicker = () => {
    const native = this.dateNative;
    if (!native || native.disabled) return;
    native.value = toIsoDate(this.dateText?.value ?? '');
    try {
      native.showPicker();
    } catch {
      native.focus();
      native.click();
    }
  };

  #onNativeDate = () => {
    if (!this.dateNative || !this.dateText) return;
    const value = fromIsoDate(this.dateNative.value);
    if (!value) return;
    this.dateText.value = value;
    if (this.#submitted) this.#validate();
    this.dateText.focus();
  };

  /**
   * Shows the fields for the current choice. Fields that get hidden are cleared, like the live form's
   * `shouldUnregister` behaviour.
   */
  #sync() {
    const isEvent = this.purchaseType === 'evento';
    const eventType = isEvent ? this.eventType : '';
    const isGraduation = eventType === this.graduationValue;

    this.form.querySelectorAll('.mrb-pt__option').forEach((option) => {
      const radio = option.querySelector('input');
      option.classList.toggle('is-selected', !!radio?.checked);
    });

    this.#togglePanel(this.eventPanel, isEvent);
    this.#togglePanel(this.otherPanel, isEvent && eventType === this.otherValue);
    this.#togglePanel(this.graduationPanel, isGraduation);
    if (this.quotePanel) this.quotePanel.hidden = !(isEvent && !!eventType && !isGraduation);
    if (this.#submitted) this.#validate();
  }

  /**
   * @param {Element | null | undefined} panel
   * @param {boolean} visible
   */
  #togglePanel(panel, visible) {
    if (!(panel instanceof HTMLElement)) return;
    const wasVisible = !panel.hidden;
    panel.hidden = !visible;
    panel.querySelectorAll('input, select').forEach((field) => {
      const control = /** @type {HTMLInputElement | HTMLSelectElement} */ (field);
      control.disabled = !visible;
      if (!visible && wasVisible) {
        if (control instanceof HTMLSelectElement) control.selectedIndex = 0;
        else control.value = '';
      }
    });
    if (!visible) panel.querySelectorAll('[data-pt-error]').forEach((error) => this.#setError(error, ''));
  }

  /**
   * @param {boolean} [focusFirstError]
   * @returns {boolean}
   */
  #validate(focusFirstError = false) {
    let firstInvalid = /** @type {HTMLElement | null} */ (null);
    this.form.querySelectorAll('[data-pt-field]').forEach((element) => {
      const field = /** @type {HTMLInputElement | HTMLSelectElement} */ (element);
      const error = this.querySelector(`[data-pt-error="${field.name}"]`);
      const active = !field.disabled && !field.closest('[hidden]');
      const message = active && field.value === '' ? field.dataset.requiredMessage || '' : '';
      field.classList.toggle('is-invalid', !!message);
      field.setAttribute('aria-invalid', message ? 'true' : 'false');
      if (error) this.#setError(error, message);
      if (message && !firstInvalid) firstInvalid = field;
    });
    if (firstInvalid && focusFirstError) /** @type {HTMLElement} */ (firstInvalid).focus();
    return !firstInvalid;
  }

  /**
   * @param {Element} error
   * @param {string} message
   */
  #setError(error, message) {
    if (!(error instanceof HTMLElement)) return;
    error.textContent = message;
    error.hidden = !message;
    const field = this.form.querySelector(`[aria-describedby="${error.id}"]`);
    if (!message && field) {
      field.classList.remove('is-invalid');
      field.setAttribute('aria-invalid', 'false');
    }
  }

  /** @returns {EventData} */
  #eventData() {
    /** @param {string} name */
    const value = (name) => {
      const field = /** @type {HTMLInputElement | null} */ (this.form.elements.namedItem(name));
      return field && !field.disabled ? field.value : undefined;
    };
    /** @type {EventData} */
    const data = { eventType: this.eventType };
    for (const name of ['eventTypeOther', 'schoolName', 'graduateName', 'tableNumber', 'eventDate']) {
      const fieldValue = value(name);
      if (fieldValue !== undefined) data[/** @type {keyof EventData} */ (name)] = fieldValue;
    }
    return data;
  }

  #onConfirm = async (event) => {
    event.preventDefault();
    if (this.#loading) return;

    /** @type {EventData | null} */
    let data = null;
    if (this.purchaseType === 'evento') {
      this.#submitted = true;
      if (!this.#validate(true)) return;
      data = this.#eventData();
    }

    this.#setLoading(true);
    const attributes = purchaseAttributes(data, { otherValue: this.otherValue, graduationValue: this.graduationValue });
    try {
      await fetch(this.dataset.cartUpdateUrl || '/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes: attributesPayload(attributes) }),
      });
    } catch {
      // The live storefront continues to checkout even when saving the attributes fails.
    }
    window.location.href = this.dataset.checkoutUrl || '/checkout';
  };

  /** @param {boolean} loading */
  #setLoading(loading) {
    this.#loading = loading;
    this.querySelectorAll('[data-pt-cancel], [data-pt-confirm]').forEach((button) => {
      /** @type {HTMLButtonElement} */ (button).disabled = loading;
    });
    this.confirmButton?.classList.toggle('is-loading', loading);
    if (this.confirmLabel) {
      this.confirmLabel.textContent = (loading ? this.confirmLabel.dataset.loading : this.confirmLabel.dataset.idle) || '';
    }
  }
}

if (!customElements.get('mrb-purchase-type')) customElements.define('mrb-purchase-type', MrbPurchaseType);
